import { styled } from 'dripsy';
import React, { useCallback } from 'react';
import { Platform, View } from 'react-native';
import { ModalComponentProp } from 'react-native-modalfy';
import { observer } from 'mobx-react-lite';

import { Post } from '@models/Posts/PostModel';
import { ModalStackParamsType } from '../../../modals/types';
import { ModalView } from '../components/ModalView';
import { Line } from '../../common/Line';
import {
  CommentInput,
  CommentInputModeEnum,
} from '../../business/CommentInput';
import { PostViewImages } from './PostViewImages';
import { PostViewCommentList } from './PostViewCommentList';
import { PostViewText } from './PostViewText';
import { User } from '@models/Viewer/UserModel';

interface IModalViewContainerStyleProps {
  hasImages: boolean;
}

const ModalViewContainer = styled(ModalView)(
  (props: IModalViewContainerStyleProps) => ({
    width: Platform.select<string | number>({
      web: props.hasImages ? '80vw' : '40vw',
      default: props.hasImages ? 1200 : 600,
    }),
    height: Platform.select<string | number>({
      web: '80vh',
      default: 600,
    }),
  }),
);

const SectionsContainer = styled(View)({
  flex: 1,
  flexDirection: 'row',
});

const ContentContainer = styled(View)({
  flex: 1,
});

const CommentInputContainer = styled(View)({
  p: '$6',
});

export interface IPostViewModalParams {
  item: Post;
  imageIndex?: number;
  onNavigateToUser?(user: User): void;
}

type ModalPropsType = ModalComponentProp<
  ModalStackParamsType,
  void,
  'PostViewModal'
>;

export const PostViewModal = observer(({ modal }: ModalPropsType) => {
  const initialImageIndex = modal.getParam('imageIndex') ?? 0;
  const item = modal.getParam('item');
  const onNavigateToUser = modal.getParam('onNavigateToUser');

  const closeModal = useCallback(() => modal.closeModal(), []);

  function onPressUser(user: User) {
    if (onNavigateToUser) {
      closeModal();
      onNavigateToUser(user);
    }
  }

  return (
    <ModalViewContainer hasImages={item.itemData.hasImages}>
      <SectionsContainer>
        {item.itemData.hasImages && (
          <PostViewImages
            initialIndex={initialImageIndex}
            item={item}
          />
        )}
        <ContentContainer>
          <PostViewText
            item={item}
            closeModal={closeModal}
            onPressUser={onPressUser}
          />
          <PostViewCommentList
            onPressUser={onPressUser}
            item={item}
          />
          <Line />
          <CommentInputContainer>
            <CommentInput
              target={{
                entity: item.comments,
                mode: CommentInputModeEnum.Create,
              }}
            />
          </CommentInputContainer>
        </ContentContainer>
      </SectionsContainer>
    </ModalViewContainer>
  );
});
